import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import {
  getFormattedDate,
  handleCsvExport,
  mappedCSVData,
} from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { useDeleteAppointment, useGetAppointments } from "../api-clients";
import { AppointmentType } from "./appointment-types";
import useAppointmentTableColumns from "./useAppointmentTableColumns";

const AppointmentsList = () => {
  useSetActiveTab(TAB_NAMES.APPOINTMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);

  const {
    isLoading,
    error,
    data,
    mutate: updateAppointmentData,
    isValidating,
  } = useGetAppointments({ length, page, search: debouncedSearch });

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteAppointment,
  } = useDeleteAppointment();

  const { isRefreshing, handleRefresh } = useManualRefresh(
    updateAppointmentData
  );

  const appointmentData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const mappedAppointmentData = useMemo(() => {
    return (appointmentData as AppointmentType[]).map(
      (appointment: AppointmentType) => ({
        ...appointment,
        appointmentDate: appointment.appointmentDate
          ? getFormattedDate(appointment.appointmentDate)
          : "",
        timeSlot: `${moment(appointment.startTime, "HH:mm:ss").format(
          "hh:mm A"
        )} - ${moment(appointment.endTime, "HH:mm:ss").format("hh:mm A")}`,
      })
    );
  }, [appointmentData]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useAppointmentTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateAppointmentData();
      setIsDeleteModalOpen(false);
      successNotification("Appointment Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateAppointmentData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteAppointment(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Appointment"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () =>
              handleCsvExport(mappedCSVData(mappedAppointmentData)),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Appointment",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={mappedAppointmentData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default AppointmentsList;
